// Global activity dock — pinned to the top of the app shell while
// background work is in flight for the active brand. Two sources,
// merged:
//   1. /api/progress/active — the unified OperationRun feed (rich:
//      stage, pct, cancellable). Grows to cover every process as the
//      instrumentation waves land.
//   2. onboarding-status.liveActivity — the legacy single-line signal
//      (detect queue / enrichment). Shown only when no OperationRun
//      already covers the brand, so nothing loses visibility during
//      rollout.
//
// Runs render as compact rows: label · stage · progress bar (or
// indeterminate) · elapsed · Stop (cancellable kinds). Self-hides when
// idle; 2s cadence while anything runs, 12s when quiet.

import { useRef, useState } from 'react';
import { Box, HStack, Text, Spinner, Icon, Progress, Button, VStack, Badge } from '@chakra-ui/react';
import { CheckCircle2, StopCircle } from 'lucide-react';
import { apiJson } from '../auth/apiFetch';
import { useBrand } from '../brand/BrandContext';
import { usePoll } from './usePoll';

type LiveActivity = { active: boolean; stage: string | null; sub: string | null };

export type OperationRunDto = {
  _id: string;
  kind: string;
  label?: string | null;
  status: 'running' | 'cancelling' | 'succeeded' | 'failed' | 'cancelled';
  stage?: string | null;
  note?: string | null;
  pct?: number | null;
  itemsDone?: number | null;
  itemsTotal?: number | null;
  cancellable?: boolean;
  startedAt: string;
  endedAt?: string | null;
  heartbeatAt?: string | null;
  error?: string | null;
};

const POLL_ACTIVE_MS = 2000;
const POLL_IDLE_MS = 12000;
const COMPLETED_FLASH_MS = 4000;
// Heartbeats stop when a process dies — show the run as stalled rather
// than eternally "running".
const STALL_AFTER_MS = 2 * 60 * 1000;

const KIND_LABEL: Record<string, string> = {
  'catalog-sync': 'Catalog sync',
  'social-ingest': 'Social posts ingest',
  'demo-sync': 'Demo data sync',
  enrichment: 'Brand enrichment',
  'category-inference': 'Category inference',
  'font-ingest': 'Font ingest',
  'campaign-sync': 'Campaign sync',
  'scheduled-sync': 'Scheduled sync',
  'ad-batch': 'Ad generation',
  'ad-regenerate': 'Ad regenerate',
  'veo-video': 'Video generation',
  render: 'Render',
  detect: 'Media detect',
  'ai-layout': 'AI layouts',
};

function runLabel(run: OperationRunDto) {
  return run.label || KIND_LABEL[run.kind] || run.kind;
}

function elapsed(run: OperationRunDto) {
  const ms = Date.now() - new Date(run.startedAt).getTime();
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function isStalled(run: OperationRunDto) {
  if (run.status !== 'running' && run.status !== 'cancelling') return false;
  const hb = run.heartbeatAt ? new Date(run.heartbeatAt).getTime() : new Date(run.startedAt).getTime();
  return Date.now() - hb > STALL_AFTER_MS;
}

export function ActivityBar() {
  const { activeBrand } = useBrand();
  const brandId = activeBrand?.id || null;
  const [completedFlash, setCompletedFlash] = useState(false);
  const wasActiveRef = useRef(false);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Runs the user asked to stop — suppresses their completion flash so a
  // cancel never reads as "finished successfully" (SalesDemos pattern).
  const cancelledByUserRef = useRef<Set<string>>(new Set());
  const [cancelInFlight, setCancelInFlight] = useState<Set<string>>(new Set());

  const { data, refresh } = usePoll(
    async () => {
      const [progress, onboarding] = await Promise.all([
        apiJson<{ runs: OperationRunDto[] }>(`/api/progress/active${brandId ? `?brandId=${brandId}` : ''}`)
          .catch(() => ({ runs: [] as OperationRunDto[] })),
        brandId
          ? apiJson<{ liveActivity: LiveActivity }>(`/api/brand/${brandId}/onboarding-status`).catch(() => null)
          : Promise.resolve(null),
      ]);
      return { runs: progress.runs || [], live: onboarding?.liveActivity ?? null };
    },
    {
      interval: (d) => {
        const active =
          (d?.runs || []).some((r) => r.status === 'running' || r.status === 'cancelling') || !!d?.live?.active;
        return active ? POLL_ACTIVE_MS : POLL_IDLE_MS;
      },
      enabled: true,
    }
  );

  const runs = (data?.runs || []).filter((r) => r.status === 'running' || r.status === 'cancelling');
  const live = data?.live ?? null;
  // Legacy sliver only when the unified feed shows nothing (rollout gap).
  const showLegacy = !!live?.active && runs.length === 0;
  const anyActive = runs.length > 0 || showLegacy;

  // active → idle transition: brief "All caught up" flash (suppressed
  // when the last active run was one the user cancelled).
  if (wasActiveRef.current && !anyActive && data) {
    const suppressed = [...cancelledByUserRef.current].length > 0 && runs.length === 0;
    if (!suppressed && !completedFlash) {
      setCompletedFlash(true);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setCompletedFlash(false), COMPLETED_FLASH_MS);
    }
    cancelledByUserRef.current.clear();
  }
  wasActiveRef.current = anyActive;

  const cancelRun = async (run: OperationRunDto) => {
    if (!window.confirm(`Stop "${runLabel(run)}"? Work already completed is kept.`)) return;
    cancelledByUserRef.current.add(run._id);
    setCancelInFlight((s) => new Set(s).add(run._id));
    try {
      await apiJson(`/api/progress/${run._id}/cancel`, { method: 'POST' });
    } catch {
      cancelledByUserRef.current.delete(run._id);
    } finally {
      setCancelInFlight((s) => {
        const next = new Set(s);
        next.delete(run._id);
        return next;
      });
      refresh();
    }
  };

  if (!anyActive && !completedFlash) return null;

  if (anyActive) {
    return (
      <Box
        position="sticky" top={0} zIndex={50}
        bg="rsViolet.500"
        color="white"
        borderBottomWidth="1px"
        borderBottomColor="rsViolet.600"
        px={4}
        py={runs.length ? 1.5 : 2}
      >
        <VStack spacing={1} maxW="1280px" mx="auto" align="stretch">
          {runs.map((run) => {
            const pct =
              run.pct != null
                ? Math.round(run.pct * 100)
                : run.itemsTotal
                  ? Math.round(((run.itemsDone || 0) / run.itemsTotal) * 100)
                  : null;
            const stalled = isStalled(run);
            const stopping = run.status === 'cancelling' || cancelInFlight.has(run._id);
            return (
              <HStack key={run._id} spacing={3} minH="26px">
                {stalled ? (
                  <Badge fontSize="9px" colorScheme="orange">stalled</Badge>
                ) : (
                  <Spinner size="xs" thickness="2px" speed="0.8s" emptyColor="whiteAlpha.300" color="white" />
                )}
                <Text fontSize="sm" fontWeight="600" noOfLines={1} flexShrink={0}>
                  {runLabel(run)}
                </Text>
                {run.stage && (
                  <Text fontSize="xs" color="whiteAlpha.800" noOfLines={1}>
                    · {run.stage}{run.note ? ` — ${run.note}` : ''}
                  </Text>
                )}
                <Box flex="1" minW="60px" maxW="260px">
                  <Progress
                    value={pct ?? undefined}
                    isIndeterminate={pct == null && !stalled}
                    size="xs"
                    borderRadius="full"
                    bg="whiteAlpha.300"
                    colorScheme="whiteAlpha"
                  />
                </Box>
                {pct != null ? (
                  <Text fontSize="xs" fontFamily="mono" color="whiteAlpha.900" flexShrink={0}>
                    {run.itemsTotal ? `${run.itemsDone ?? 0}/${run.itemsTotal}` : `${pct}%`}
                  </Text>
                ) : run.itemsDone ? (
                  // Unknown total (e.g. paginated ingest) — live counter.
                  <Text fontSize="xs" fontFamily="mono" color="whiteAlpha.900" flexShrink={0}>
                    {run.itemsDone} items
                  </Text>
                ) : null}
                <Text fontSize="xs" color="whiteAlpha.700" flexShrink={0}>{elapsed(run)}</Text>
                {run.cancellable && (
                  // Explicit white chip w/ violet text. The themed `outline`
                  // variant hard-codes bg:white (reachSocialTheme), so an
                  // outline button with color:white here rendered white-on-white
                  // and was unreadable on the violet bar.
                  <Button
                    size="xs"
                    bg="whiteAlpha.900"
                    color="rsViolet.700"
                    _hover={{ bg: 'white' }}
                    _active={{ bg: 'whiteAlpha.800' }}
                    leftIcon={<Icon as={StopCircle} boxSize={3} />}
                    isDisabled={stopping}
                    onClick={() => void cancelRun(run)}
                  >
                    {stopping ? 'stopping…' : 'Stop'}
                  </Button>
                )}
              </HStack>
            );
          })}
          {showLegacy && live && (
            <HStack spacing={3}>
              <Spinner size="sm" thickness="2px" speed="0.8s" emptyColor="whiteAlpha.300" color="white" />
              <Text fontSize="sm" fontWeight="600" noOfLines={1}>{live.stage}</Text>
              {live.sub && (
                <Text fontSize="xs" color="whiteAlpha.800" noOfLines={1}>· {live.sub}</Text>
              )}
            </HStack>
          )}
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      position="sticky" top={0} zIndex={50}
      bg="green.500"
      color="white"
      borderBottomWidth="1px"
      borderBottomColor="green.600"
      px={4}
      py={2}
    >
      <HStack spacing={3} maxW="1280px" mx="auto">
        <Icon as={CheckCircle2} boxSize={4} />
        <Text fontSize="sm" fontWeight="600">All caught up</Text>
      </HStack>
    </Box>
  );
}
